/**
 * Money is stored and calculated in whole paise (integers), never as floating-point rupees,
 * so totals always add up to the paisa.
 */
export type Paise = number;

export function isPaise(value: unknown): value is Paise {
  return typeof value === 'number' && Number.isSafeInteger(value);
}

export function assertPaise(value: unknown, label = 'amount'): asserts value is Paise {
  if (!isPaise(value)) throw new TypeError(`${label} must be a whole number of paise`);
}

/** ₹ amount (as typed or parsed) → paise, rounded to the nearest paisa. */
export function rupeesToPaise(rupees: number): Paise {
  if (!Number.isFinite(rupees)) throw new TypeError('rupees must be a finite number');
  // Scale through a string to avoid 1.005 * 100 = 100.49999… style errors.
  const [whole = '0', frac = ''] = Math.abs(rupees).toFixed(6).split('.');
  const paise = Number(whole) * 100 + Math.round(Number(`${frac.slice(0, 2)}.${frac.slice(2)}`));
  return rupees < 0 ? -paise : paise;
}

export function paiseToRupees(paise: Paise): number {
  assertPaise(paise);
  return paise / 100;
}

/** Percentage of an amount in paise, rounded half away from zero to the nearest paisa. */
export function percentOf(amount: Paise, percent: number): Paise {
  assertPaise(amount);
  if (!Number.isFinite(percent)) throw new TypeError('percent must be a finite number');
  const exact = (amount * percent) / 100;
  return Math.sign(exact) * Math.round(Math.abs(exact));
}

const INR_WHOLE = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const INR_EXACT = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** ₹1,23,456 when there are no paise; ₹1,23,456.50 when there are (or always, with exact). */
export function formatPaise(amount: Paise, options: { exact?: boolean } = {}): string {
  assertPaise(amount);
  const rupees = amount / 100;
  return options.exact || amount % 100 !== 0 ? INR_EXACT.format(rupees) : INR_WHOLE.format(rupees);
}

/** Words for multiples of a rupee amount, as people type them: "1.2 lakh", "60 hazaar", "5k", "2 cr". */
export const AMOUNT_UNITS: Readonly<Record<string, number>> = Object.freeze({
  k: 1_000,
  thousand: 1_000,
  thousands: 1_000,
  hazaar: 1_000,
  hazar: 1_000,
  hajar: 1_000,
  hazzar: 1_000,
  हज़ार: 1_000,
  हजार: 1_000,
  l: 1_00_000,
  lac: 1_00_000,
  lacs: 1_00_000,
  lakh: 1_00_000,
  lakhs: 1_00_000,
  लाख: 1_00_000,
  cr: 1_00_00_000,
  crore: 1_00_00_000,
  crores: 1_00_00_000,
  करोड़: 1_00_00_000,
  करोड: 1_00_00_000,
});

const UNIT_PATTERN = Object.keys(AMOUNT_UNITS)
  .sort((a, b) => b.length - a.length)
  .join('|');

/** A rupee amount with an optional unit, e.g. "1,20,000", "1.2 lakh", "₹ 85,000/-". Anchored: the whole text. */
const AMOUNT_ONLY = new RegExp(
  `^(?:₹|rs\\.?|inr|rupees?)?\\s*(\\d+(?:,\\d+)*(?:\\.\\d+)?)\\s*(${UNIT_PATTERN})?\\s*(?:/-|rupees?|rs\\.?|₹)?$`,
  'iu',
);

/** Spoken fractions of a thousand, lakh or crore, bounded so names and other quantities stay literal. */
export function normaliseAmountWords(text: string): string {
  const values: Record<string, string> = {
    dedh: '1.5',
    derh: '1.5',
    डेढ़: '1.5',
    डेढ: '1.5',
    dhai: '2.5',
    ढाई: '2.5',
    sava: '1.25',
    sawa: '1.25',
    सवा: '1.25',
    'paune do': '1.75',
    'paunay do': '1.75',
    'पौने दो': '1.75',
  };
  // NFC splits ज़ into ज and its dot, so the unit allows either.
  const units: [RegExp, string][] = [
    [/^(?:lakhs?|lacs?|लाख)$/iu, 'lakh'],
    [/^(?:thousands?|hazaa?r|hajar|hazzar|हज\u093C?ार|हज़ार)$/iu, 'thousand'],
    [/^(?:crores?|करोड़?|करोड\u093C?)$/iu, 'crore'],
  ];
  return text
    .normalize('NFC')
    .replace(
      /(?<![\p{L}\p{M}\p{N}])(dedh|derh|डेढ़|डेढ|dhai|ढाई|sava|sawa|सवा|paune\s+do|paunay\s+do|पौने\s+दो)\s+(lakhs?|lacs?|लाख|thousands?|hazaa?r|hajar|hazzar|हज\u093C?ार|हज़ार|crores?|करोड\u093C?|करोड़)(?![\p{L}\p{M}\p{N}])/giu,
      (whole, fraction: string, unit: string) => {
        const value = values[fraction.toLowerCase().replace(/\s+/g, ' ')] ?? values[fraction.normalize('NFC')];
        const name = units.find(([re]) => re.test(unit))?.[1];
        return value && name ? `${value} ${name}` : whole;
      },
    );
}

/**
 * The amount in a short answer such as "1.2 lakh", "60 hazaar", "₹85,000/-" or "1,20,000", in paise.
 * Null when the text isn't just an amount. Devanagari digits count.
 */
export function readRupees(text: string): Paise | null {
  const s = normaliseAmountWords(text)
    .normalize('NFC')
    .replace(/[०-९]/g, (d) => String('०१२३४५६७८९'.indexOf(d)))
    .trim()
    .replace(/\s+/g, ' ');
  const m = AMOUNT_ONLY.exec(s);
  if (!m) return null;
  const digits = (m[1] ?? '').replace(/,/g, '');
  const unit = m[2] ? (AMOUNT_UNITS[m[2].toLowerCase()] ?? AMOUNT_UNITS[m[2]] ?? 1) : 1;
  const rupees = Number(digits) * unit;
  if (!Number.isFinite(rupees) || rupees <= 0 || rupees > 1e11) return null;
  return rupeesToPaise(rupees);
}
